import { DiaryCompletenessCard } from "@/components/nutrition/DiaryCompletenessCard";
import type { DiaryCompleteness } from "@/types";
import { invalidateNutritionQueries } from "@/lib/nutritionQueries";
import { ProvisionalTargetNote } from "./ProvisionalTargetNote";
/**
 * Phase 24.4b Step 2 — Today tab of the Nutrition page.
 *
 * Extracted from `pages/Nutrition.tsx` to free that file to host a
 * top-level Tabs container (Today vs Meal Plan) in Step 3. Single-file
 * extraction: this file owns the entire Today experience including the
 * date-range sub-tabs (today/yesterday/week/month) and all helper
 * sub-components.
 *
 * Notable refactor decision: the existing internal `tab` variable
 * (formerly line 91 of Nutrition.tsx, the date-range view selector) has
 * been renamed to `viewMode` to disambiguate from the new top-level
 * `?tab=` URL state that Step 3 will introduce.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Apple,
  Coffee,
  Utensils,
  Moon,
  X,
  Info,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "@/api/client";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { CalorieBalanceCard } from "@/components/nutrition/CalorieBalanceCard";
import { HydrationCard } from "@/components/nutrition/HydrationCard";
import { EnergyAvailabilityCard } from "@/components/nutrition/EnergyAvailabilityCard";
import { PhotoMealUpload } from "@/components/nutrition/PhotoMealUpload";
import {
  createMeal,
  deleteMeal,
  getSummaryToday,
  getWeightTrend,
  listTodayMeals,
  logWeight,
  getNutritionProfile,
  updateNutritionProfile,
} from "@/api/nutrition";
import type { MealEntry } from "@/types";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/dates";
import { THEME } from "@/lib/theme";

const MEAL_TYPE_META: Record<
  MealEntry["meal_type"],
  { label: string; icon: LucideIcon }
> = {
  breakfast: { label: "meal_type.breakfast", icon: Coffee },
  lunch: { label: "meal_type.lunch", icon: Utensils },
  dinner: { label: "meal_type.dinner", icon: Moon },
  snack: { label: "meal_type.snack", icon: Apple },
  pre_workout: { label: "meal_type.pre_workout", icon: Apple },
  post_workout: { label: "meal_type.post_workout", icon: Apple },
};

const MEAL_TYPES = Object.keys(MEAL_TYPE_META) as MealEntry["meal_type"][];

function macroBar(cur: number, target: number, color: string) {
  const pct = target > 0 ? Math.max(0, Math.min(120, (cur / target) * 100)) : 0;
  const over = pct > 100;
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-cream-border">
      <div
        className={cn("h-full rounded-full", over ? "bg-coral-dark" : color)}
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  );
}

export default function NutritionTodayTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const summaryQ = useQuery({
    queryKey: ["nutrition-summary"],
    queryFn: getSummaryToday,
    staleTime: 10_000,
  });
  const mealsQ = useQuery({
    queryKey: ["nutrition-meals", "today"],
    queryFn: listTodayMeals,
    staleTime: 10_000,
  });
  const weightQ = useQuery({
    queryKey: ["nutrition-weight-trend"],
    queryFn: () => getWeightTrend(30),
    staleTime: 60_000,
  });
  const profileQ = useQuery({
    queryKey: ["nutrition-profile"],
    queryFn: getNutritionProfile,
    staleTime: 60_000,
  });

  const [addMealOpen, setAddMealOpen] = useState(false);
  const [targetOpen, setTargetOpen] = useState(false);
  const [viewMode, setViewMode] = useState<
    "today" | "yesterday" | "week" | "month"
  >("today");

  const delMeal = useMutation({
    mutationFn: (id: number) => deleteMeal(id),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["nutrition-meals"] }),
        invalidateNutritionQueries(qc),
        qc.invalidateQueries({ queryKey: ["dashboard"] }),
      ]),
  });

  const summary = summaryQ.data;
  const targets = summary?.targets;
  const meals = mealsQ.data ?? [];
  const weight = weightQ.data;
  const profile = profileQ.data;

  return (
    <div className="space-y-5">
      <header className="flex items-baseline justify-between gap-2">
        <div>
          <SectionLabel>{t("nutrition_page.section_label")}</SectionLabel>
          <h1 className="mt-1 font-serif text-[26px] font-medium leading-snug md:text-[30px]">
            {viewMode === "today"
              ? t("nutrition_page.today_with_date", { date: fmtDate(new Date().toISOString(), "d MMMM") })
              : viewMode === "yesterday"
                ? t("nutrition_page.yesterday")
                : viewMode === "week"
                  ? t("nutrition_page.week")
                  : t("nutrition_page.month")}
          </h1>
        </div>
      </header>

      <div className="flex gap-1 overflow-x-auto scrollbar-hide border-b border-cream-border">
        {([
          ["today", t("nutrition_page.today")],
          ["yesterday", t("nutrition_page.yesterday")],
          ["week", t("nutrition_page.week")],
          ["month", t("nutrition_page.month")],
        ] as const).map(([k, lbl]) => (
          <button
            key={k}
            type="button"
            onClick={() => setViewMode(k)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-[13px] transition-colors ${
              viewMode === k
                ? "border-amber font-medium text-navy-deep"
                : "border-transparent text-taupe hover:text-navy-deep"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {viewMode !== "today" ? (
        <NutritionArchiveInline viewMode={viewMode} />
      ) : (
        <>
          <CalorieBalanceCard />
          <HydrationCard />
          <EnergyAvailabilityCard />
        </>
      )}

      {viewMode === "today" ? (
        <>
      {/* Today summary */}
      <Card>
        {!summary ? (
          <p className="text-[13px] text-taupe">{t("nutrition_page.loading")}</p>
        ) : (
          <>
            <div className="flex items-baseline justify-between">
              <div>
                <div className="metric-number text-[32px] text-navy-deep">
                  {summary.total_calories}
                </div>
                <div className="text-[11px] text-taupe">
                  {t("nutrition_page.of_kcal", { n: targets?.target_calories ?? 0 })}
                </div>
              </div>
              <div className="text-right text-[11px] text-taupe">
                <div>
                  {t("nutrition_page.n_meals_other", { count: summary.meals_count })}
                </div>
                {targets?.training_burn ? (
                  <div>{t("nutrition_page.training_burn_added", { n: targets.training_burn })}</div>
                ) : null}
              </div>
            </div>
            <ProvisionalTargetNote quality={targets} />
            <div className="mt-2">
              {macroBar(
                summary.total_calories,
                targets?.target_calories ?? 0,
                "bg-navy",
              )}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
              <MacroRow
                label={t("nutrition_page.macro_protein")}
                cur={summary.total_protein_g}
                target={targets?.target_protein_g ?? 0}
                color="bg-coral"
              />
              <MacroRow
                label={t("nutrition_page.macro_carbs")}
                cur={summary.total_carbs_g}
                target={targets?.target_carbs_g ?? 0}
                color="bg-amber"
              />
              <MacroRow
                label={t("nutrition_page.macro_fat")}
                cur={summary.total_fat_g}
                target={targets?.target_fat_g ?? 0}
                color="bg-taupe"
              />
            </div>

            {targets?.reasoning ? (
              <button
                type="button"
                onClick={() => setTargetOpen((v) => !v)}
                className="mt-3 flex items-center gap-1 text-[11px] text-taupe hover:text-navy-deep"
              >
                <Info className="h-3 w-3" strokeWidth={1.8} />
                {t("nutrition_page.how_targets")}
              </button>
            ) : null}
            {targetOpen && targets ? (
              <div className="mt-2 rounded-md border border-cream-border bg-cream-light p-3 text-[12px] text-taupe">
                {targets.reasoning}
              </div>
            ) : null}
          </>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={() => setAddMealOpen(true)}
            className="inline-flex items-center gap-1"
          >
            <Plus className="h-4 w-4" strokeWidth={2} />
            {t("nutrition_page.add_meal")}
          </Button>

        </div>
      </Card>

      <PhotoMealUpload />

      {/* Weight card */}
      {weight && weight.current_kg != null ? (
        <Card>
          <div className="flex items-center justify-between">
            <SectionLabel>{t("nutrition_page.weight_section")}</SectionLabel>
            <span className="text-[11px] text-taupe">
              {t("nutrition_page.n_records_30d", { n: weight.points.length })}
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="metric-number text-[28px] text-navy-deep">
              {weight.current_kg}
            </span>
            <span className="text-[12px] text-taupe">{t("nutrition_page.kg")}</span>
            {weight.week_avg_kg != null ? (
              <span className="ml-auto text-[12px] text-taupe">
                {t("nutrition_page.avg_7d")} <span className="metric-number">{weight.week_avg_kg}</span>
              </span>
            ) : null}
            {weight.target_kg != null ? (
              <span className="text-[12px] text-taupe">
                {t("nutrition_page.goal")} <span className="metric-number">{weight.target_kg}</span>
              </span>
            ) : null}
          </div>
        </Card>
      ) : null}

      {/* Today meals list */}
      <Card>
        <SectionLabel>{t("nutrition_page.today_meals")}</SectionLabel>
        {meals.length === 0 ? (
          <p className="mt-3 text-[13px] text-taupe">
            {t("nutrition_page.no_meals_yet")}
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {meals.map((m) => (
              <MealRow key={m.id} meal={m} onDelete={() => delMeal.mutate(m.id)} />
            ))}
          </ul>
        )}
      </Card>

      {/* Dietary profile hint */}
      {profile && !profile.target_weight_kg ? (
        <Card>
          <SectionLabel>{t("nutrition_page.no_target_weight_title")}</SectionLabel>
          <p className="mt-2 text-[13px] text-taupe">
            {t("nutrition_page.no_target_weight_desc")}
          </p>
          <NutritionProfileQuickSet profile={profile} onSaved={() => qc.invalidateQueries({ queryKey: ["nutrition-profile"] })} />
        </Card>
      ) : null}

        </>
      ) : null}

      {addMealOpen ? (
        <AddMealModal
          onClose={() => setAddMealOpen(false)}
          onSaved={() => {
            setAddMealOpen(false);
            qc.invalidateQueries({ queryKey: ["nutrition-meals"] });
            invalidateNutritionQueries(qc);
            qc.invalidateQueries({ queryKey: ["dashboard"] });
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline tabs that reuse the existing NutritionArchive content but render
// inside the main Nutrition page (no route navigation needed).
// ---------------------------------------------------------------------------

function NutritionArchiveInline({
  viewMode,
}: {
  viewMode: "yesterday" | "week" | "month";
}) {
  if (viewMode === "yesterday") return <YesterdayInline />;
  if (viewMode === "week") return <WeekInline />;
  return <MonthInline />;
}

function YesterdayInline() {
  const { t } = useTranslation();
  const yesterdayIso = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  })();
  const q = useQuery({
    queryKey: ["nutrition-day", yesterdayIso],
    queryFn: async () => {
      const { data } = await api.get(`/nutrition/day/${yesterdayIso}`);
      return data as DayDetail;
    },
  });
  if (q.isLoading) return <div className="py-6 text-center text-taupe">{t("nutrition_page.loading")}</div>;
  if (!q.data) return <div className="py-6 text-center text-coral">{t("nutrition_page.load_failed")}</div>;
  if (q.data.meal_count === 0 && q.data.completeness?.status !== "complete") {
    return (
      <Card>
        <div className="py-2 text-center">
          <div className="font-medium text-navy-deep">{t("nutrition_page.no_yesterday_title")}</div>
          <div className="mt-1 text-[13px] text-taupe">
            {t("nutrition_page.no_yesterday_desc")}
          </div>
        </div>
      </Card>
    );
  }
  const d = q.data;
  const over = d.deviation_kcal != null && d.deviation_kcal > d.target_kcal * 0.1;
  const under = d.deviation_kcal != null && d.deviation_kcal < -d.target_kcal * 0.1;
  return (
    <div className="space-y-4">
      <DiaryCompletenessCard date={d.date} completeness={d.completeness} />
      <Card>
        <div className="text-[11px] uppercase tracking-wider text-amber-dark">
          {t("nutrition_page.n_intake_meals", { date: d.date, n: d.meal_count })}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <KStat label={t("nutrition_page.eaten")} value={`${d.intake_kcal}`} sub={t("nutrition_page.kcal")} />
          <KStat label={t("nutrition_page.target")} value={`${d.target_kcal}`} sub={t("nutrition_page.kcal")} />
          <KStat
            label="Δ"
            value={`${d.deviation_kcal != null && d.deviation_kcal > 0 ? "+" : ""}${d.deviation_kcal ?? "—"}`}
            sub={d.deviation_kcal == null ? t("diary_quality.unknown") : over ? t("nutrition_page.over_target") : under ? t("nutrition_page.under_target") : t("nutrition_page.in_target")}
            color={over ? "coral" : under ? "amber" : "navy"}
          />
        </div>
        <div className="mt-4 space-y-2 border-t border-cream-border pt-3">
          <MacroRowYesterday label={t("nutrition_page.macro_protein")} current={d.protein_g} target={d.target_protein_g} unit={t("nutrition_page.g")} />
          <MacroRowYesterday label={t("nutrition_page.macro_carbs")} current={d.carbs_g} target={d.target_carbs_g} unit={t("nutrition_page.g")} />
          <MacroRowYesterday label={t("nutrition_page.macro_fat")} current={d.fat_g} target={d.target_fat_g} unit={t("nutrition_page.g")} />
          {d.fiber_g != null ? (
            <MacroRowYesterday label={t("nutrition_page.macro_fiber")} current={d.fiber_g} target={25} unit={t("nutrition_page.g")} />
          ) : null}
        </div>
      </Card>
      {d.adequacy?.flags?.length ? (
        <Card>
          <SectionLabel>{t("nutrition_page.coach_noticed")}</SectionLabel>
          <ul className="mt-3 space-y-3">
            {d.adequacy.flags.map((flag, i) => (
              <li key={i}>
                <div className="text-[13px] font-medium text-navy-deep">
                  {flag.issue}
                </div>
                {flag.suggestion ? (
                  <div className="text-[12px] text-taupe">→ {flag.suggestion}</div>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

function MacroRowYesterday({
  label,
  current,
  target,
  unit,
}: {
  label: string;
  current: number;
  target: number;
  unit: string;
}) {
  const { t } = useTranslation();
  const pct = target > 0 ? (current / target) * 100 : 0;
  const dev = current - target;
  const devPct = target > 0 ? (dev / target) * 100 : 0;
  const status =
    Math.abs(devPct) <= 10
      ? { icon: "✓", text: t("nutrition_page.in_target_short"), color: "text-navy-deep" }
      : devPct > 30
        ? { icon: "↑↑", text: t("nutrition_page.over_short", { n: Math.round(dev), unit }), color: "text-coral" }
        : devPct > 10
          ? { icon: "↑", text: `+${Math.round(dev)}${unit}`, color: "text-amber-dark" }
          : devPct < -30
            ? { icon: "↓↓", text: t("nutrition_page.under_short", { n: Math.round(dev), unit }), color: "text-coral" }
            : { icon: "↓", text: `${Math.round(dev)}${unit}`, color: "text-amber-dark" };
  return (
    <div>
      <div className="flex items-center justify-between text-[12px]">
        <span className="text-taupe">{label}</span>
        <span className={`metric-number ${status.color}`}>
          {Math.round(current)}/{Math.round(target)}
          {unit}
          <span className="ml-2 text-[11px]">
            {status.icon} {status.text}
          </span>
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-cream-border">
        <div
          className={`h-full ${pct > 110 ? "bg-coral/60" : pct < 70 ? "bg-amber/40" : "bg-amber/70"}`}
          style={{ width: `${Math.min(120, pct)}%` }}
        />
      </div>
    </div>
  );
}

function KStat({
  label,
  value,
  sub,
  color = "navy",
}: {
  label: string;
  value: string;
  sub?: string;
  color?: "navy" | "coral" | "amber";
}) {
  const { t } = useTranslation();
  const c = color === "coral" ? "text-coral" : color === "amber" ? "text-amber-dark" : "text-navy-deep";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-taupe">{label}</div>
      <div className={`metric-number text-[26px] ${c}`}>{value}</div>
      {sub ? <div className="text-[11px] text-taupe">{sub}</div> : null}
    </div>
  );
}

function WeekInline() {
  const { t } = useTranslation();
  const monday = (() => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d.toISOString().slice(0, 10);
  })();
  const q = useQuery({
    queryKey: ["nutrition-week-inline", monday],
    queryFn: async () => {
      const { data } = await api.get(`/nutrition/archive/week-summary?week_start=${monday}`);
      return data as WeekSummaryInline;
    },
  });
  if (q.isLoading) return <div className="py-6 text-center text-taupe">{t("nutrition_page.loading")}</div>;
  if (!q.data) return <div className="py-6 text-center text-coral">{t("common.error_short")}</div>;
  const d = q.data;
  return (
    <div className="space-y-4">
      {(d.complete_days ?? 0) < 7 ? (
        <div className="rounded-xl border-l-4 border-amber bg-amber/10 p-4 text-[13px]">
          <div className="font-medium text-navy-deep">
            {t("diary_quality.coverage", { n: d.complete_days ?? 0 })}
          </div>
          <div className="mt-1 text-taupe">
            {t("diary_quality.explanation")}
          </div>
        </div>
      ) : null}

      {d.logged_days === 0 && !(d.complete_days ?? 0) ? (
        <Card>
          <p className="text-center text-[13px] text-taupe">{t("nutrition_page.no_data_week")}</p>
        </Card>
      ) : (
        <>
          <Card>
            <SectionLabel>{t("nutrition_page.kcal_by_day")}</SectionLabel>
            <CalorieBars days={d.days} />
          </Card>
          <Card>
            <SectionLabel>{t("nutrition_page.macros_week")}</SectionLabel>
            <div className="mt-3 space-y-4">
              <MacroWeekBars
                label={t("nutrition_page.macro_protein")}
                days={d.days}
                getValue={(x) => x.protein_g}
                target={averageMacroTarget(d.days, "target_protein_g")}
                unit={t("nutrition_page.g")}
                color="#B87E24"
              />
              <MacroWeekBars
                label={t("nutrition_page.macro_carbs")}
                days={d.days}
                getValue={(x) => x.carbs_g}
                target={averageMacroTarget(d.days, "target_carbs_g")}
                unit={t("nutrition_page.g")}
                color="#5B8C5A"
              />
              <MacroWeekBars
                label={t("nutrition_page.macro_fat")}
                days={d.days}
                getValue={(x) => x.fat_g}
                target={averageMacroTarget(d.days, "target_fat_g")}
                unit={t("nutrition_page.g")}
                color={THEME.ink}
              />
            </div>
          </Card>
          {d.insights && d.insights.length > 0 ? (
            <Card>
              <SectionLabel>{t("nutrition_page.what_we_noticed")}</SectionLabel>
              <ul className="mt-3 space-y-3">
                {d.insights.map((insight, i) => (
                  <li key={i}>
                    <div
                      className={`text-[13px] font-medium ${
                        insight.severity === "high"
                          ? "text-coral"
                          : insight.severity === "medium"
                            ? "text-amber-dark"
                            : "text-navy-deep"
                      }`}
                    >
                      {insight.severity === "high" ? "⚠ " : insight.severity === "medium" ? "↗ " : "✓ "}
                      {insight.title}
                    </div>
                    <div className="mt-1 text-[12px] text-taupe">{insight.description}</div>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

function averageMacroTarget(days: DayInline[], key: "target_protein_g" | "target_carbs_g" | "target_fat_g") {
  const targets = days.map(day => day[key]).filter((value): value is number => typeof value === "number" && value > 0);
  return targets.length ? Math.round(targets.reduce((sum, value) => sum + value, 0) / targets.length) : 0;
}

interface DayInline {
  target_protein_g?: number;
  target_carbs_g?: number;
  target_fat_g?: number;
  date: string;
  intake_kcal: number;
  target_kcal: number;
  deviation_kcal: number | null;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

interface WeekSummaryInline {
  week_start: string;
  week_end: string;
  logged_days: number;
  complete_days?: number;
  averages: { intake_kcal: number; target_kcal: number; protein_g: number; carbs_g: number; fat_g: number } | null;
  days: DayInline[];
  insights?: Array<{ severity: string; title: string; description: string }>;
}

interface DayDetail {
  date: string;
  completeness?: DiaryCompleteness;
  meal_count: number;
  intake_kcal: number;
  target_kcal: number;
  deviation_kcal: number | null;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  target_protein_g: number;
  target_carbs_g: number;
  target_fat_g: number;
  adequacy?: {
    flags: Array<{ macro: string; severity: string; issue: string; suggestion?: string }>;
  };
}

function _wdShort(idx: number, t: (k: string) => string): string {
  return [
    t("nutrition_page.wd_short_mon"),
    t("nutrition_page.wd_short_tue"),
    t("nutrition_page.wd_short_wed"),
    t("nutrition_page.wd_short_thu"),
    t("nutrition_page.wd_short_fri"),
    t("nutrition_page.wd_short_sat"),
    t("nutrition_page.wd_short_sun"),
  ][idx] || "";
}

function buildWeekArr(days: DayInline[], weekStart: string): (DayInline | null)[] {
  const map = new Map(days.map((d) => [d.date, d]));
  const start = new Date(weekStart);
  const out: (DayInline | null)[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    out.push(map.get(iso) || null);
  }
  return out;
}

function CalorieBars({ days }: { days: DayInline[] }) {
  const { t } = useTranslation();
  if (days.length === 0) return null;
  const weekStart = days.reduce(
    (m, d) => (d.date < m ? d.date : m),
    days[0].date,
  );
  const sortedAsc = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
  const arr = buildWeekArr(sortedAsc, sortedAsc[0]?.date ?? weekStart);
  const maxV = Math.max(
    ...sortedAsc.map((d) => Math.max(d.intake_kcal, d.target_kcal)),
    1,
  );
  return (
    <div className="mt-3">
      <div className="flex items-end gap-2" style={{ height: 140 }}>
        {arr.map((d, i) => {
          if (!d) {
            return (
              <div key={i} className="flex flex-1 flex-col items-center justify-end gap-1">
                <div className="w-full rounded-t bg-cream-border/40" style={{ height: "4%" }} />
                <span className="text-[10px] text-taupe">{_wdShort(i, t)}</span>
              </div>
            );
          }
          const intakePct = (d.intake_kcal / maxV) * 100;
          const targetPct = (d.target_kcal / maxV) * 100;
          const over = d.deviation_kcal != null && d.deviation_kcal > d.target_kcal * 0.1;
          const under = d.deviation_kcal != null && d.deviation_kcal < -d.target_kcal * 0.1;
          const fill = d.deviation_kcal == null ? "bg-cream-border" : over ? "bg-coral/60" : under ? "bg-amber/40" : "bg-amber/70";
          return (
            <div
              key={i}
              className="relative flex flex-1 flex-col items-center justify-end gap-1"
              title={`${d.date}: ${d.intake_kcal}/${d.target_kcal} kcal`}
            >
              <div className="relative w-full" style={{ height: "100%" }}>
                <div
                  className="absolute left-0 right-0 border-t border-dashed border-taupe/40"
                  style={{ bottom: `${targetPct}%` }}
                />
                <div
                  className={`absolute bottom-0 left-0 right-0 rounded-t ${fill}`}
                  style={{ height: `${intakePct}%` }}
                />
              </div>
              <span className="text-[10px] text-taupe">{_wdShort(i, t)}</span>
              <span className="metric-number text-[10px] text-navy-deep">
                {Math.round(d.intake_kcal / 100) * 100}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MacroWeekBars({
  label,
  days,
  getValue,
  target,
  unit,
  color,
}: {
  label: string;
  days: DayInline[];
  getValue: (d: DayInline) => number;
  target: number;
  unit: string;
  color: string;
}) {
  const { t } = useTranslation();
  if (days.length === 0) return null;
  const sortedAsc = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
  const arr = buildWeekArr(sortedAsc, sortedAsc[0].date);
  const max = Math.max(target * 1.3, ...sortedAsc.map(getValue));
  const avg =
    Math.round(
      sortedAsc.reduce((s, d) => s + getValue(d), 0) / sortedAsc.length,
    );
  const adherence = sortedAsc.filter((d) => Math.abs(getValue(d) - target) <= target * 0.15).length;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <div>
          <span className="text-[13px] font-medium text-navy-deep">{label}</span>
          <span className="ml-2 text-[11px] text-taupe">
            {t("nutrition_page.avg_target", { avg, unit, target })}
          </span>
        </div>
        <span className="text-[11px] text-taupe">
          {t("nutrition_page.n_in_target", { n: adherence, total: sortedAsc.length })}
        </span>
      </div>
      <div className="mt-1 flex items-end gap-1" style={{ height: 64 }}>
        {arr.map((d, i) => {
          const v = d ? getValue(d) : 0;
          const h = (v / max) * 100;
          const inTarget = d && Math.abs(v - target) <= target * 0.15;
          return (
            <div
              key={i}
              className="flex-1 rounded-t"
              style={{
                height: d ? `${Math.max(h, 4)}%` : "2%",
                minHeight: "2px",
                backgroundColor: color,
                opacity: !d ? 0.15 : inTarget ? 1 : 0.55,
              }}
              title={d ? `${_wdShort(i, t)}: ${Math.round(v)}${unit}` : t("nutrition_page.no_data")}
            />
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-taupe">
        {[0,1,2,3,4,5,6].map((i) => (
          <span key={i} className="flex-1 text-center">
            {_wdShort(i, t)}
          </span>
        ))}
      </div>
    </div>
  );
}

function MonthInline() {
  const { t } = useTranslation();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const q = useQuery({
    queryKey: ["nutrition-month-inline", year, month],
    queryFn: async () => {
      const { data } = await api.get(`/nutrition/archive/month-summary?year=${year}&month=${month}`);
      return data as MonthSummaryInline;
    },
  });

  const shift = (delta: number) => {
    let m = month + delta;
    let y = year;
    while (m < 1) {
      m += 12;
      y -= 1;
    }
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    setYear(y);
    setMonth(m);
  };
  const isCurrent = year === today.getFullYear() && month === today.getMonth() + 1;

  if (q.isLoading) return <div className="py-6 text-center text-taupe">{t("nutrition_page.loading")}</div>;
  if (!q.data) return <div className="py-6 text-center text-coral">{t("nutrition_page.load_failed")}</div>;
  const d = q.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => shift(-1)}
          className="rounded-md border border-cream-border bg-cream-light px-3 py-1.5 text-[13px] hover:bg-cream"
        >
          ← {t("nutrition_page.previous")}
        </button>
        <span className="text-[13px] text-navy-deep">
          {d.month_name} {d.year}
        </span>
        <button
          type="button"
          onClick={() => shift(1)}
          disabled={isCurrent}
          className="rounded-md border border-cream-border bg-cream-light px-3 py-1.5 text-[13px] hover:bg-cream disabled:opacity-40"
        >
          {t("nutrition_page.next")} →
        </button>
      </div>

      <Card>
        <SectionLabel>{t("nutrition_page.summary")}</SectionLabel>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          <KStat label={t("diary_quality.confirmed_days")} value={`${d.complete_days ?? 0}/${d.expected_days}`} sub={`${d.completion_pct}%`} />
          <KStat label={t("diary_quality.avg_confirmed")} value={`${d.averages.intake_kcal ?? "—"}`} sub={t("nutrition_page.kcal")} />
          {d.averages.protein_g_per_kg != null ? (
            <KStat
              label={t("nutrition_page.protein_short")}
              value={`${d.averages.protein_g_per_kg}`}
              sub={t("nutrition_page.g_per_kg")}
              color={d.averages.protein_g_per_kg >= 1.6 ? "navy" : "amber"}
            />
          ) : null}
          {d.vs_previous_month?.delta_kcal != null ? (
            <KStat
              label={t("nutrition_page.vs_prev")}
              value={`${d.vs_previous_month.delta_kcal > 0 ? "+" : ""}${d.vs_previous_month.delta_kcal}`}
              sub={t("nutrition_page.kcal_per_day")}
              color={d.vs_previous_month.delta_kcal > 200 ? "coral" : d.vs_previous_month.delta_kcal < -200 ? "amber" : "navy"}
            />
          ) : null}
        </div>
      </Card>

      <Card>
        <SectionLabel>{t("nutrition_page.calendar")}</SectionLabel>
        <CalendarHeatInline year={d.year} month={d.month} days={d.days} />
      </Card>
    </div>
  );
}

interface MonthSummaryInline {
  year: number;
  month: number;
  month_name: string;
  logged_days: number;
  complete_days?: number;
  expected_days: number;
  completion_pct: number;
  averages: { intake_kcal: number | null; protein_g_per_kg: number | null };
  vs_previous_month: { previous_avg_intake_kcal: number | null; delta_kcal: number | null };
  days: DayInline[];
}

function CalendarHeatInline({
  year,
  month,
  days,
}: {
  year: number;
  month: number;
  days: DayInline[];
}) {
  const { t } = useTranslation();
  const map = new Map(days.map((d) => [d.date, d]));
  const firstOfMonth = new Date(year, month - 1, 1);
  const totalDays = new Date(year, month, 0).getDate();
  const startCol = (firstOfMonth.getDay() + 6) % 7;
  const cells: Array<{ key: string; day: DayInline | null; dom: number | null }> = [];
  for (let i = 0; i < startCol; i++)
    cells.push({ key: `lead-${i}`, day: null, dom: null });
  for (let dom = 1; dom <= totalDays; dom++) {
    const dt = new Date(year, month - 1, dom);
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    cells.push({ key: iso, day: map.get(iso) || null, dom });
  }
  while (cells.length % 7 !== 0)
    cells.push({ key: `tail-${cells.length}`, day: null, dom: null });

  return (
    <div className="mt-3">
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-taupe">
        {[0,1,2,3,4,5,6].map((i) => (
          <div key={i} className="py-1">
            {_wdShort(i, t)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c) => {
          if (c.dom == null)
            return <div key={c.key} className="aspect-square" aria-hidden="true" />;
          const dd = c.day;
          if (!dd) {
            return (
              <div
                key={c.key}
                className="flex aspect-square flex-col rounded-md border border-cream-border bg-cream-border/30 p-1.5 text-taupe/60"
              >
                <span className="text-[11px] font-medium">{c.dom}</span>
              </div>
            );
          }
          const over = dd.deviation_kcal != null && dd.deviation_kcal > dd.target_kcal * 0.1;
          const under = dd.deviation_kcal != null && dd.deviation_kcal < -dd.target_kcal * 0.1;
          const tone = dd.deviation_kcal == null ? "border-cream-border bg-cream-light" : over
            ? "border-coral/40 bg-coral/15"
            : under
              ? "border-amber/30 bg-amber/15"
              : "border-amber/40 bg-amber/30";
          return (
            <Link
              key={c.key}
              to={`/nutrition/day/${dd.date}`}
              className={`flex aspect-square flex-col justify-between rounded-md border p-1.5 transition-shadow hover:shadow-soft ${tone}`}
              title={`${dd.intake_kcal}/${dd.target_kcal} kcal`}
            >
              <span className="text-[11px] font-medium text-navy-deep">{c.dom}</span>
              <div className="text-right">
                <div className="metric-number text-[10px] leading-none text-navy-deep">
                  {dd.intake_kcal}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-taupe">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm bg-amber/30" /> {t("nutrition_page.in_target")}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm bg-coral/40" /> {t("nutrition_page.over_target")}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm bg-amber/15" /> {t("nutrition_page.under_target")}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm bg-cream-border" /> {t("nutrition_page.no_data")}
        </span>
      </div>
    </div>
  );
}

function MacroRow({
  label,
  cur,
  target,
  color,
}: {
  label: string;
  cur: number;
  target: number;
  color: string;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="mb-1 flex justify-between text-[11px]">
        <span className="font-medium text-navy-deep">{label}</span>
        <span className="metric-number text-taupe">
          {cur}{t("nutrition_page.g")} / {target}{t("nutrition_page.g")}
        </span>
      </div>
      {macroBar(cur, target, color)}
    </div>
  );
}

function MealRow({
  meal,
  onDelete,
}: {
  meal: MealEntry;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const meta = MEAL_TYPE_META[meal.meal_type];
  const Icon = meta?.icon ?? Apple;
  return (
    <li className="flex items-start gap-3 rounded-lg border border-cream-border bg-cream p-3 transition-colors hover:border-amber/50">
      <Link
        to={`/nutrition/meals/${meal.id}`}
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber/15 text-amber-dark hover:bg-amber/25"
        title={t("nutrition_page.edit_components")}
      >
        <Icon className="h-4 w-4" strokeWidth={1.8} />
      </Link>
      <Link
        to={`/nutrition/meals/${meal.id}`}
        className="min-w-0 flex-1"
      >
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wider text-amber-dark">
            {meta?.label ?? meal.meal_type}
          </span>
          {meal.time ? (
            <span className="text-[11px] text-taupe">· {meal.time.slice(0, 5)}</span>
          ) : null}
        </div>
        {meal.description ? (
          <div className="mt-0.5 text-[13px] text-navy-deep">{meal.description}</div>
        ) : null}
        <div className="mt-1 text-[11px] text-taupe">
          <span className="metric-number text-navy-deep">{meal.calories}</span> {t("nutrition_page.kcal")} ·
          {t("nutrition_page.p")} <span className="metric-number">{meal.protein_g}</span> ·
          {t("nutrition_page.c")} <span className="metric-number">{meal.carbs_g}</span> ·
          {t("nutrition_page.f")} <span className="metric-number">{meal.fat_g}</span>
        </div>
      </Link>
      <button
        type="button"
        onClick={onDelete}
        className="rounded-md border border-cream-border bg-cream-light p-1.5 text-coral-dark hover:bg-coral/10"
        title={t("nutrition_page.delete_meal")}
      >
        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
      </button>
    </li>
  );
}

function AddMealModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [mealType, setMealType] = useState<MealEntry["meal_type"]>("lunch");
  const [description, setDescription] = useState("");
  const [calories, setCalories] = useState(0);
  const [protein, setProtein] = useState(0);
  const [carbs, setCarbs] = useState(0);
  const [fat, setFat] = useState(0);
  const now = useMemo(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes(),
    ).padStart(2, "0")}`;
  }, []);
  const [timeStr, setTimeStr] = useState(now);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (calories <= 0) {
      setError(t("nutrition_page.specify_calories"));
      return;
    }
    setSaving(true);
    try {
      await createMeal({
        meal_type: mealType,
        description,
        calories,
        protein_g: protein,
        carbs_g: carbs,
        fat_g: fat,
        time: timeStr ? `${timeStr}:00` : null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("nutrition_page.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-deep/60 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-cream-border bg-cream-light p-5 shadow-soft"
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <SectionLabel>{t("nutrition_page.meal_section")}</SectionLabel>
            <h3 className="mt-1 font-serif text-[20px] font-medium">{t("nutrition_page.new")}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-taupe hover:text-navy-deep"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-taupe">{t("nutrition_page.type")}</label>
              <select
                value={mealType}
                onChange={(e) =>
                  setMealType(e.target.value as MealEntry["meal_type"])
                }
                className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[14px]"
              >
                {MEAL_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {MEAL_TYPE_META[t].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-taupe">{t("nutrition_page.time")}</label>
              <Input
                type="time"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-taupe">
              {t("nutrition_page.description")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[14px]"
              placeholder={t("nutrition_page.description_placeholder")}
            />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <NumberField label={t("nutrition_page.kcal_label")} value={calories} onChange={setCalories} min={0} max={6000} />
            <NumberField label={t("nutrition_page.p_g")} value={protein} onChange={setProtein} min={0} max={500} />
            <NumberField label={t("nutrition_page.c_g")} value={carbs} onChange={setCarbs} min={0} max={1000} />
            <NumberField label={t("nutrition_page.f_g")} value={fat} onChange={setFat} min={0} max={500} />
          </div>
          {error ? <p className="text-[13px] text-coral-dark">{error}</p> : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            {t("nutrition_page.cancel")}
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? t("nutrition_page.saving") : t("nutrition_page.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
}) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-taupe">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        min={min}
        max={max}
        className="mt-1 w-full rounded-md border border-cream-border bg-cream px-2 py-2 text-[14px] metric-number"
      />
    </div>
  );
}

function LogWeightModal({
  initialWeight,
  onClose,
  onSaved,
}: {
  initialWeight: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [weight, setWeight] = useState(initialWeight ?? 85);
  const [bodyFat, setBodyFat] = useState<number | "">("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (weight < 30 || weight > 250) {
      setError(t("nutrition_page.weight_range_error"));
      return;
    }
    setSaving(true);
    try {
      await logWeight({
        weight_kg: weight,
        body_fat_pct: typeof bodyFat === "number" ? bodyFat : undefined,
        notes: notes || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("nutrition_page.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-deep/60 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-cream-border bg-cream-light p-5 shadow-soft"
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <SectionLabel>{t("nutrition_page.weight_section")}</SectionLabel>
            <h3 className="mt-1 font-serif text-[20px] font-medium">{t("nutrition_page.weight_morning")}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-taupe hover:text-navy-deep"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-taupe">
              {t("nutrition_page.weight_kg_label")}
            </label>
            <input
              type="number"
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value) || 0)}
              className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[16px] metric-number"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-taupe">
              {t("nutrition_page.fat_pct_known")}
            </label>
            <input
              type="number"
              step="0.1"
              value={bodyFat}
              onChange={(e) => setBodyFat(e.target.value ? Number(e.target.value) : "")}
              className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[14px] metric-number"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-taupe">
              {t("nutrition_page.note")}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-cream-border bg-cream px-3 py-2 text-[14px]"
              placeholder={t("nutrition_page.note_placeholder")}
            />
          </div>
          {error ? <p className="text-[13px] text-coral-dark">{error}</p> : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            {t("nutrition_page.cancel")}
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? t("nutrition_page.saving") : t("nutrition_page.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function NutritionProfileQuickSet({
  profile,
  onSaved,
}: {
  profile: import("@/types").NutritionProfile;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState(profile.target_weight_kg || 85);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await updateNutritionProfile({ target_weight_kg: target });
      onSaved();
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-3 flex items-end gap-2">
      <div>
        <label className="text-[11px] uppercase tracking-wider text-taupe">
          {t("nutrition_page.target_weight_kg")}
        </label>
        <input
          type="number"
          step="0.5"
          value={target}
          onChange={(e) => setTarget(Number(e.target.value) || 0)}
          className="mt-1 w-32 rounded-md border border-cream-border bg-cream px-3 py-2 text-[14px] metric-number"
        />
      </div>
      <Button onClick={submit} disabled={saving}>
        {saving ? "…" : t("nutrition_page.save")}
      </Button>
    </div>
  );
}
